import React, { useCallback, useMemo, useRef, useState } from "react";
import { autoUpdate, flip, useClick, useDismiss, useFloating, useInteractions, useListNavigation, useRole, offset } from "@floating-ui/react";
import { QuiMenuContext } from "./QuiMenuContext.js";
export function QuiMenu({ children, placement, disabled, enableFlip, onOpen, onClose }) {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const listRef = useRef([]);
    const onOpenChange = useCallback((newIsOpen) => {
        setIsOpen(newIsOpen);
        if (newIsOpen) {
            onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        }
        else {
            onClose === null || onClose === void 0 ? void 0 : onClose();
            setActiveIndex(0);
        }
    }, [onOpen, onClose]);
    const close = useCallback(() => {
        onOpenChange(false);
    }, [onOpenChange]);
    const middleware = useMemo(() => {
        if (enableFlip) {
            return [offset(10), flip()];
        }
        return [offset(10)];
    }, [enableFlip]);
    const { context, refs, strategy, floatingStyles } = useFloating({
        open: isOpen && !disabled,
        onOpenChange,
        whileElementsMounted: autoUpdate,
        middleware,
        strategy: "fixed",
        placement,
    });
    const onNavigate = useCallback((index) => {
        setActiveIndex(index);
    }, []);
    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "menu" });
    const listNavigation = useListNavigation(context, {
        activeIndex,
        onNavigate,
        listRef,
        loop: true,
        focusItemOnOpen: true,
        selectedIndex: activeIndex,
        orientation: "vertical",
    });
    const { getFloatingProps, getItemProps, getReferenceProps } = useInteractions([click, dismiss, listNavigation, role]);
    const menuContextValue = useMemo(() => ({
        activeIndex,
        buttonRef: refs.reference,
        isOpen,
        close,
        floatingContext: context,
        floatingRef: refs.floating,
        getFloatingProps,
        getItemProps,
        getReferenceProps,
        listRef,
        strategy,
        floatingStyles,
        refs,
    }), [activeIndex, floatingStyles, refs, isOpen, close, context, getFloatingProps, getItemProps, getReferenceProps, listRef, strategy]);
    return (React.createElement(QuiMenuContext.Provider, { value: menuContextValue },
        React.createElement("div", { className: "qui-menu-wrapper" }, children)));
}
